import { createSlice } from '@reduxjs/toolkit';

const fixMetadataSlice = createSlice({
    name: 'fixMetadata',
    initialState: {
        files: [],
        metadataComparisons: [], // Array of { filePath, current: {}, database: {}, selected: true, status: 'pending' }
        isProcessing: false,
        progress: 0,
        cancelRequested: false,
        backupBeforeWrite: true,
        // How suggestions treat values you already have:
        //   'fillEmpty'     — only fill empty fields, never touch existing (default)
        //   'overwriteText' — replace existing text tags, but never your keys
        //   'overwriteAll'  — replace everything, keys included
        writeMode: 'fillEmpty',
        // Clean label/website junk out of titles and split "Artist - Title" using
        // MixGoat's beautify rules. On by default — it only refines, never invents.
        beautify: true,
        autoFix: false,
        editingFile: null, // For manual editing modal
        // Metadata field selection (all enabled by default)
        enabledFields: {
            artist: true,
            title: true,
            album: true,
            albumArtist: true,
            label: true,
            releaseDate: true,
            genre: true,
            bpm: true,
            key: true,
        },
        dateFormat: 'fullDate', // 'fullDate' | 'monthAndYear' | 'year' | 'dmyDot' | 'dmySlash' | 'mdySlash'
        filenameMode: 'both', // 'none' | 'prettify' | 'fixFromDb' | 'both'
        autoAnalyze: false,
        // "Click any row to edit" toolbar tip — one-time teaching, dismiss is
        // FOREVER (persisted). One shared flag for both sub-tabs: it's the
        // same affordance, dismissing on Tags shouldn't re-pitch on Filenames.
        hideEditTip: false,
    },
    reducers: {
        setFiles: (state, action) => {
            state.files = action.payload;
            state.metadataComparisons = [];
            state.progress = 0;
        },
        setMetadataComparisons: (state, action) => {
            state.metadataComparisons = action.payload;
        },
        // Lay out one placeholder row per file the analyzer is about to walk,
        // in walk order, so the table has its final shape before the first
        // result lands. Results replace these in place (upsertMetadataComparisons
        // matches by filePath); anything still pending when the run ends is
        // dropped by clearPendingComparisons.
        setPendingComparisons: (state, action) => {
            state.metadataComparisons = action.payload.map((p) => ({
                order: p.order,
                filePath: p.filePath,
                fileName: p.fileName,
                pending: true,
                current: {},
                database: {},
                selected: false,
                hasChanges: false,
                isWav: /\.wav$/i.test(p.fileName || ''),
                rekordbox: null,
                metadataSource: 'none',
                filenameByMode: null,
                suggestedFilename: null,
            }));
        },
        clearPendingComparisons: (state) => {
            state.metadataComparisons = state.metadataComparisons.filter((r) => !r.pending);
        },
        // Streamed analysis rows. Each finished file arrives on its own (the
        // pool finishes them out of order), so a row replaces an existing one
        // for the same file or is inserted at its walk position (`order`) —
        // the table reads top-to-bottom in file order the whole run, exactly
        // as the old full-snapshot ticks did.
        upsertMetadataComparisons: (state, action) => {
            const list = state.metadataComparisons;
            for (const row of action.payload) {
                const existing = list.findIndex(r => r.filePath === row.filePath);
                if (existing !== -1) { list[existing] = row; continue; }
                const order = row.order ?? Infinity;
                let at = list.length;
                while (at > 0 && (list[at - 1].order ?? -1) > order) at--;
                list.splice(at, 0, row);
            }
        },
        // "Remove from list" on a row menu — drops the file from this session's
        // comparison view AND from the pending write set. Nothing on disk or in
        // the local DB is touched.
        removeComparisonRow: (state, action) => {
            const filePath = action.payload;
            state.metadataComparisons = state.metadataComparisons.filter(item => item.filePath !== filePath);
            state.files = state.files.filter(f => f !== filePath);
        },
        updateMetadataComparison: (state, action) => {
            const { filePath, data } = action.payload;
            const index = state.metadataComparisons.findIndex(item => item.filePath === filePath);
            if (index !== -1) {
                state.metadataComparisons[index] = { ...state.metadataComparisons[index], ...data };
            }
        },
        applyWrittenTags: (state, action) => {
            const { filePath, tagsWritten } = action.payload;
            const TAG_TO_FIELD = {
                artist: 'artist',
                title: 'title',
                album: 'album',
                performerInfo: 'albumArtist',
                publisher: 'label',
                recordingTime: 'releaseDate',
                genre: 'genre',
                bpm: 'bpm',
                initialKey: 'key',
                trackNumber: 'track',
                composer: 'composer',
                partOfSet: 'discNumber',
            };
            const index = state.metadataComparisons.findIndex(item => item.filePath === filePath);
            if (index !== -1) {
                const item = state.metadataComparisons[index];
                const newCurrent = { ...item.current };
                for (const [tagKey, fieldKey] of Object.entries(TAG_TO_FIELD)) {
                    if (tagsWritten[tagKey] !== undefined) {
                        newCurrent[fieldKey] = String(tagsWritten[tagKey]);
                    }
                }
                state.metadataComparisons[index] = {
                    ...item,
                    current: newCurrent,
                    hasChanges: false,
                    selected: false,
                };
            }
        },
        toggleFileSelection: (state, action) => {
            const filePath = action.payload;
            const item = state.metadataComparisons.find(item => item.filePath === filePath);
            if (item && !item.pending) {
                item.selected = !item.selected;
            }
        },
        selectAllFiles: (state) => {
            state.metadataComparisons.forEach(item => {
                if (!item.pending) item.selected = true;
            });
        },
        deselectAllFiles: (state) => {
            state.metadataComparisons.forEach(item => {
                item.selected = false;
            });
        },
        setIsProcessing: (state, action) => {
            state.isProcessing = action.payload;
        },
        setProgress: (state, action) => {
            state.progress = action.payload;
        },
        setCancelRequested: (state, action) => {
            state.cancelRequested = action.payload;
        },
        setBackupBeforeWrite: (state, action) => {
            state.backupBeforeWrite = action.payload;
        },
        setWriteMode: (state, action) => {
            state.writeMode = action.payload;
        },
        setBeautify: (state, action) => {
            state.beautify = action.payload;
        },
        setAutoFix: (state, action) => {
            state.autoFix = action.payload;
        },
        setEditingFile: (state, action) => {
            state.editingFile = action.payload;
        },
        toggleMetadataField: (state, action) => {
            const field = action.payload;
            if (state.enabledFields.hasOwnProperty(field)) {
                state.enabledFields[field] = !state.enabledFields[field];
            }
        },
        setDateFormat: (state, action) => {
            state.dateFormat = action.payload;
        },
        resetFilePath: (state) => {
            state.files = [];
            state.metadataComparisons = [];
            state.progress = 0;
            state.isProcessing = false;
            state.cancelRequested = false;
        },
        setFilenameMode: (state, action) => {
            state.filenameMode = action.payload;
        },
        setMetadataAutoAnalyze: (state, action) => {
            state.autoAnalyze = action.payload;
        },
        dismissEditTip: (state) => {
            state.hideEditTip = true;
        },
        applyFileRename: (state, action) => {
            const { filePath, newPath, newName } = action.payload;
            // Update the comparison entry with the new file path
            const index = state.metadataComparisons.findIndex(item => item.filePath === filePath);
            if (index !== -1) {
                state.metadataComparisons[index] = {
                    ...state.metadataComparisons[index],
                    filePath: newPath,
                    fileName: newName,
                    suggestedFilename: null,
                    filenameByMode: null, // Clear all cached suggestions after rename
                };
            }
            // Update the files array
            const fileIndex = state.files.indexOf(filePath);
            if (fileIndex !== -1) {
                state.files[fileIndex] = newPath;
            }
        },
        // Merge persisted settings loaded from localStorage at startup
        // (whitelisted subset — see settingsPersistenceConfig; excludes the
        // transient file/comparison/progress state also held in this slice).
        hydrate(state, action) {
            Object.assign(state, action.payload || {});
        },
    },
});

export const {
    setFiles,
    setMetadataComparisons,
    upsertMetadataComparisons,
    setPendingComparisons,
    clearPendingComparisons,
    removeComparisonRow,
    updateMetadataComparison,
    applyWrittenTags,
    toggleFileSelection,
    selectAllFiles,
    deselectAllFiles,
    setIsProcessing,
    setProgress,
    setCancelRequested,
    setBackupBeforeWrite,
    setWriteMode,
    setBeautify,
    setAutoFix,
    setEditingFile,
    toggleMetadataField,
    setDateFormat,
    resetFilePath,
    setFilenameMode,
    setMetadataAutoAnalyze,
    dismissEditTip,
    applyFileRename,
    hydrate,
} = fixMetadataSlice.actions;

export default fixMetadataSlice.reducer;
